import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import {
  Alert,
  AlertTitle,
  Avatar,
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  Link,
  Stack,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Copyright } from "../Copyright";
import { useAuth } from "./ParseAuthContext.tsx";
import { useTheme } from "../../context/ThemeContext";
import { InvalidCredentialsError, MfaRequiredError } from "./AuthContext";
import { PasswordField } from "./PasswordField";

const LockAvatar = styled(Avatar)`
  background-color: ${(props) => props.theme.palette.secondary.main};
  margin: ${(props) => props.theme.spacing(1)};
`;

/**
 * How long a password that was shown stays readable, so that it is not left
 * on the screen.
 */
const PASSWORD_SHOWN_FOR_MS = 5000;

type Phase =
  | { kind: "credentials" }
  | {
      kind: "mfa";
      username: string;
      password: string;
      remember: boolean;
      useRecoveryCode: boolean;
    };

export default function SignIn() {
  const { theme } = useTheme();
  const { t } = useTranslation(undefined, { keyPrefix: "login" });
  const { login, loginWithMfa, auth } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [phase, setPhase] = useState<Phase>({ kind: "credentials" });
  const [mfaToken, setMfaToken] = useState("");

  async function handleCredentialsSubmit(
    e: React.MouseEvent<HTMLButtonElement>,
  ) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(username, password, remember);
      console.debug("Login successful, redirecting to app");
    } catch (err) {
      if (err instanceof MfaRequiredError) {
        setPhase({
          kind: "mfa",
          username: err.username,
          password: err.password,
          remember: err.remember,
          useRecoveryCode: false,
        });
        setMfaToken("");
      } else {
        console.log("Failed to login", err);
        setError(
          t(
            err instanceof InvalidCredentialsError
              ? "error.invalidCredentials"
              : "error.tryAgain",
          ),
        );
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleMfaSubmit(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    if (phase.kind !== "mfa") return;
    setError("");
    setLoading(true);
    try {
      await loginWithMfa(
        phase.username,
        phase.password,
        mfaToken.trim(),
        phase.remember,
      );
    } catch (err) {
      console.log("Failed MFA login", err);
      setError(
        t(
          phase.useRecoveryCode
            ? "mfa.error.invalidRecoveryCode"
            : "mfa.error.invalidToken",
        ),
      );
    } finally {
      setLoading(false);
    }
  }

  function backToCredentials() {
    setPhase({ kind: "credentials" });
    setMfaToken("");
    setError("");
    setPassword("");
  }

  function toggleRecoveryMode() {
    if (phase.kind !== "mfa") return;
    setPhase({ ...phase, useRecoveryCode: !phase.useRecoveryCode });
    setMfaToken("");
    setError("");
  }

  const isMfaPhase = phase.kind === "mfa";
  const useRecoveryCode = isMfaPhase && phase.useRecoveryCode;

  return (
    <Container
      component="main"
      maxWidth="xs"
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        minHeight: "100vh",
        gap: (theme) => theme.spacing(2),
        py: (theme) => theme.spacing(8),
      }}
    >
      <Stack>
        <Box
          sx={{
            display: "flex",
            gap: theme.spacing(1),
            alignItems: "center",
          }}
        >
          <LockAvatar>
            <LockOutlinedIcon />
          </LockAvatar>
          <Typography component="h1" variant="h5">
            <Trans i18nKey={isMfaPhase ? "login.mfa.title" : "login.title"} />
          </Typography>
        </Box>

        {!isMfaPhase && (
          <form noValidate>
            {auth.kind === "sessionExpired" && (
              <Alert severity="info" sx={{ mt: theme.spacing(1) }}>
                <Trans i18nKey="login.sessionExpired" />
              </Alert>
            )}
            <TextField
              variant="outlined"
              margin="normal"
              disabled={loading}
              required
              fullWidth
              id="email"
              label={<Trans i18nKey="login.label.email" />}
              name="email"
              autoComplete="email"
              autoFocus
              value={username}
              onChange={(event) => setUsername(event.target.value)}
            />
            <PasswordField
              margin="normal"
              disabled={loading}
              required
              name="password"
              label={t("label.password")}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={setPassword}
              shownForMs={PASSWORD_SHOWN_FOR_MS}
            />
            <FormControlLabel
              control={
                <Checkbox
                  value="remember"
                  color="primary"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  disabled={loading}
                />
              }
              label={<Trans i18nKey="login.label.remember" />}
            />
            {error !== "" && (
              <Alert severity="error" sx={{ mb: theme.spacing(1) }}>
                <AlertTitle>
                  <Trans i18nKey="login.error.signInFailed" />
                </AlertTitle>
                {error}
              </Alert>
            )}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={
                loading ||
                username.trim().length < 1 ||
                password.trim().length < 1
              }
              onClick={handleCredentialsSubmit}
            >
              <Trans i18nKey="login.submit" />
            </Button>
          </form>
        )}

        {isMfaPhase && (
          <form noValidate>
            <Typography variant="body2" sx={{ mt: theme.spacing(1) }}>
              <Trans
                i18nKey={
                  useRecoveryCode
                    ? "login.mfa.recoveryIntro"
                    : "login.mfa.tokenIntro"
                }
              />
            </Typography>
            <TextField
              variant="outlined"
              margin="normal"
              disabled={loading}
              required
              fullWidth
              autoFocus
              label={
                <Trans
                  i18nKey={
                    useRecoveryCode
                      ? "login.mfa.recoveryLabel"
                      : "login.mfa.tokenLabel"
                  }
                />
              }
              value={mfaToken}
              onChange={(event) =>
                setMfaToken(
                  useRecoveryCode
                    ? event.target.value.trim()
                    : event.target.value.replace(/[^0-9]/g, "").slice(0, 6),
                )
              }
              slotProps={{
                htmlInput: {
                  inputMode: useRecoveryCode ? "text" : "numeric",
                  autoComplete: "one-time-code",
                },
              }}
            />
            {error !== "" && (
              <Alert severity="error" sx={{ mb: theme.spacing(1) }}>
                <AlertTitle>
                  <Trans i18nKey="login.mfa.error.title" />
                </AlertTitle>
                {error}
              </Alert>
            )}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={
                loading || mfaToken.trim().length < (useRecoveryCode ? 10 : 6)
              }
              onClick={handleMfaSubmit}
              sx={{ mt: theme.spacing(1) }}
            >
              <Trans i18nKey="login.mfa.submit" />
            </Button>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mt: theme.spacing(1),
              }}
            >
              <Link
                component="button"
                type="button"
                onClick={backToCredentials}
                underline="hover"
                disabled={loading}
              >
                <Trans i18nKey="login.mfa.back" />
              </Link>
              <Link
                component="button"
                type="button"
                onClick={toggleRecoveryMode}
                underline="hover"
                disabled={loading}
              >
                <Trans
                  i18nKey={
                    useRecoveryCode
                      ? "login.mfa.useToken"
                      : "login.mfa.useRecovery"
                  }
                />
              </Link>
            </Box>
          </form>
        )}
      </Stack>
      <Copyright />
    </Container>
  );
}
